import { useShow } from '@refinedev/core';
import { Show } from '@refinedev/antd';
import { Card, Descriptions, Tag, Row, Col, Timeline, Divider, Button } from 'antd';
import {
  EnvironmentOutlined,
  CarOutlined,
  UserOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { PostageTripDetails, CargoTripDetails } from './components';

interface PostageDestination {
  id: number;
  order: number;
  address: string;
  recipientName: string;
  recipientPhone: string;
  deliveryNote?: string;
  status: 'PENDING' | 'IN_TRANSIT' | 'DELIVERED' | 'FAILED';
  deliveredAt?: string;
  failureReason?: string;
}

interface Trip {
  id: number;
  tripType: string;
  status: string;
  pickupAddress: string;
  destinationAddress: string;
  pickupLat: number;
  pickupLong: number;
  destinationLat: number;
  destinationLong: number;
  pickupRegion: { nameUz: string } | null;
  destinationRegion: { nameUz: string } | null;
  pickupDistrict: { nameUz: string } | null;
  destinationDistrict: { nameUz: string } | null;
  distanceKm: number;
  durationSeconds: number | null;
  priceEstimate: number;
  clientOfferPrice: number | null;
  finalPrice: number | null;
  paymentMethod: string | null;
  surgeMultiplier: number | null;
  passengerCount: number;
  requestedAt: string;
  acceptedAt: string | null;
  arrivedAt: string | null;
  pickedUpAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  canceledAt: string | null;
  cancelActor: string | null;
  cancelReason: string | null;
  cancelReasonRef: {
    id: number;
    reasonUz: string;
    reasonRu: string;
    reasonEn: string;
  } | null;
  client: {
    id: number;
    fullName: string;
    phone: string;
    photoUrl: string | null;
  };
  driver: {
    id: number;
    fullName: string;
    phone: string;
    photoUrl: string | null;
    rating: number;
  } | null;
  vehicle: {
    make: string;
    model: string;
    plate: string;
    color: string;
  } | null;
  // Postage specific fields
  packageDescription?: string;
  packageWeight?: number;
  packageSize?: 'SMALL' | 'MEDIUM' | 'LARGE' | 'EXTRA_LARGE';
  isFragile?: boolean;
  requiresSignature?: boolean;
  insuranceValue?: number;
  destinations?: PostageDestination[];
  // Cargo specific fields
  cargoDescription?: string;
  cargoType?: 'GENERAL' | 'FRAGILE' | 'PERISHABLE' | 'HAZARDOUS' | 'LIVESTOCK' | 'VEHICLE';
  weight?: number;
  weightUnit?: 'KG' | 'TON';
  length?: number;
  width?: number;
  height?: number;
  volume?: number;
  requiresRefrigeration?: boolean;
  temperatureMin?: number;
  temperatureMax?: number;
  requiresSpecialHandling?: boolean;
  handlingInstructions?: string;
  insuranceRequired?: boolean;
  loadingAssistance?: boolean;
  unloadingAssistance?: boolean;
  vehicleType?: 'PICKUP' | 'VAN' | 'TRUCK_SMALL' | 'TRUCK_MEDIUM' | 'TRUCK_LARGE' | 'FLATBED' | 'REFRIGERATED';
}

const WAITING_STATUSES = ['REQUESTED', 'ACCEPTED', 'DRIVER_ARRIVED'];

const formatWaitingTime = (requestedAt: string): string => {
  const diff = Date.now() - new Date(requestedAt).getTime();
  if (diff < 0) return '0s';
  const totalSeconds = Math.floor(diff / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (days > 0) return `${days}k ${hours}s ${minutes}d`;
  if (hours > 0) return `${hours}s ${minutes}d`;
  if (minutes > 0) return `${minutes}d ${seconds}s`;
  return `${seconds}s`;
};

export const TripShow = () => {
  const { tripType, id } = useParams();
  const navigate = useNavigate();
  const [, setTick] = useState(0);
  const { queryResult } = useShow<Trip>({
    resource: `admin/trips/${tripType}`,
    id: id!,
  });
  const { data, isLoading } = queryResult;
  const trip = data?.data;

  // Live timer for waiting trips
  useEffect(() => {
    if (trip && WAITING_STATUSES.includes(trip.status)) {
      const interval = setInterval(() => setTick((t) => t + 1), 1000);
      return () => clearInterval(interval);
    }
  }, [trip?.status]);

  const formatDuration = (seconds: number | null | undefined): string => {
    if (!seconds || seconds <= 0) return '-';
    const totalMinutes = Math.round(seconds / 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours > 0 && minutes > 0) return `${hours} soat ${minutes} daq`;
    if (hours > 0) return `${hours} soat`;
    return `${minutes} daq`;
  };

  const formatCurrency = (value: number | string) => {
    return new Intl.NumberFormat('uz-UZ').format(Number(value)) + ' so\'m';
  };

  const formatDate = (date: string | null) => {
    if (!date) return '-';
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const mins = String(d.getMinutes()).padStart(2, '0');
    return `${day}.${month}.${year} ${hours}:${mins}`;
  };

  const statusLabels: Record<string, string> = {
    REQUESTED: 'So\'ralgan',
    ACCEPTED: 'Qabul qilingan',
    DRIVER_ARRIVED: 'Haydovchi yetib keldi',
    PICKED_UP: 'Olib ketildi',
    ON_THE_WAY: 'Yo\'lda',
    IN_PROGRESS: 'Jarayonda',
    COMPLETED: 'Yakunlangan',
    CANCELLED: 'Bekor qilingan',
  };

  const tripTypeLabels: Record<string, string> = {
    INTERCITY_TAXI: 'Shahar ichida taksi',
    INTERREGION_TAXI: 'Shaharlararo taksi',
    POSTAGE: 'Pochta',
    CARGO: 'Yuk tashish',
  };

  const getTimelineItems = () => {
    const items = [];

    if (trip?.requestedAt) {
      items.push({
        color: 'blue',
        children: `So'ralgan: ${formatDate(trip.requestedAt)}`,
      });
    }
    if (trip?.acceptedAt) {
      items.push({
        color: 'cyan',
        children: `Qabul qilingan: ${formatDate(trip.acceptedAt)}`,
      });
    }
    if (trip?.arrivedAt) {
      items.push({
        color: 'purple',
        children: `Haydovchi yetib keldi: ${formatDate(trip.arrivedAt)}`,
      });
    }
    if (trip?.pickedUpAt) {
      items.push({
        color: 'geekblue',
        children: `Olib ketildi: ${formatDate(trip.pickedUpAt)}`,
      });
    }
    if (trip?.startedAt) {
      items.push({
        color: 'orange',
        children: `Yo'lda: ${formatDate(trip.startedAt)}`,
      });
    }
    if (trip?.completedAt) {
      items.push({
        color: 'green',
        children: `Yakunlangan: ${formatDate(trip.completedAt)}`,
      });
    }
    if (trip?.canceledAt) {
      items.push({
        color: 'red',
        children: `Bekor qilingan: ${formatDate(trip.canceledAt)}`,
      });
    }

    return items;
  };

  return (
    <Show
      isLoading={isLoading}
      title={`Sayohat #${trip?.id}`}
      headerButtons={
        <Button
          type="primary"
          icon={<EditOutlined />}
          onClick={() => navigate(`/trips/${tripType}/${id}/edit`)}
        >
          Tahrirlash
        </Button>
      }
    >
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={16}>
          <Card
            title={
              <>
                <EnvironmentOutlined /> Marshrut
              </>
            }
          >
            <Descriptions column={1}>
              <Descriptions.Item label="Olish hududi">
                {[trip?.pickupRegion?.nameUz, trip?.pickupDistrict?.nameUz].filter(Boolean).join(', ') || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Olish manzili">
                {trip?.pickupAddress}
              </Descriptions.Item>
              <Descriptions.Item label="Borish hududi">
                {[trip?.destinationRegion?.nameUz, trip?.destinationDistrict?.nameUz].filter(Boolean).join(', ') || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Borish manzili">
                {trip?.destinationAddress}
              </Descriptions.Item>
              <Descriptions.Item label="Masofa">
                {Number(trip?.distanceKm || 0).toFixed(1)} km
              </Descriptions.Item>
              <Descriptions.Item label="Taxminiy vaqt">
                {formatDuration(trip?.durationSeconds)}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card
            title={
              <>
                <DollarOutlined /> To'lov
              </>
            }
            style={{ marginTop: 24 }}
          >
            <Descriptions column={2}>
              <Descriptions.Item label="Taxminiy narx">
                {formatCurrency(trip?.priceEstimate || 0)}
              </Descriptions.Item>
              <Descriptions.Item label="Mijoz taklifi">
                {trip?.clientOfferPrice ? formatCurrency(trip.clientOfferPrice) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Yakuniy narx">
                <strong>
                  {trip?.finalPrice != null && trip.finalPrice > 0 ? formatCurrency(trip.finalPrice) : formatCurrency(trip?.priceEstimate || 0)}
                </strong>
              </Descriptions.Item>
              <Descriptions.Item label="To'lov usuli">
                {trip?.paymentMethod || '-'}
              </Descriptions.Item>
              {trip?.surgeMultiplier && trip.surgeMultiplier > 1 && (
                <Descriptions.Item label="Surge">
                  x{trip.surgeMultiplier}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>

          <Card title="Vaqt chizig'i" style={{ marginTop: 24 }}>
            <Timeline items={getTimelineItems()} />
            {(trip?.cancelReason || trip?.cancelReasonRef || trip?.cancelActor) && (
              <>
                <Divider />
                {trip?.cancelActor && (
                  <p>
                    <strong>Bekor qiluvchi:</strong>{' '}
                    <Tag color={trip.cancelActor === 'CLIENT' ? 'orange' : trip.cancelActor === 'DRIVER' ? 'red' : 'default'}>
                      {trip.cancelActor}
                    </Tag>
                  </p>
                )}
                {trip?.cancelReasonRef && (
                  <p>
                    <strong>Sabab:</strong> {trip.cancelReasonRef.reasonUz}
                  </p>
                )}
                {trip?.cancelReason && (!trip?.cancelReasonRef || trip.cancelReason !== trip.cancelReasonRef.reasonUz) && (
                  <p>
                    <strong>{trip?.cancelReasonRef ? 'Izoh:' : 'Bekor qilish sababi:'}</strong> {trip.cancelReason}
                  </p>
                )}
              </>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card>
            <Descriptions column={1}>
              <Descriptions.Item label="Sayohat turi">
                <Tag color="blue">{tripTypeLabels[trip?.tripType || ''] || trip?.tripType}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Holat">
                <Tag
                  color={trip?.status === 'COMPLETED' ? 'green' : trip?.status === 'CANCELLED' ? 'red' : 'blue'}
                >
                  {statusLabels[trip?.status || ''] || trip?.status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Yo'lovchilar">
                {trip?.passengerCount || 1}
              </Descriptions.Item>
              {trip?.requestedAt && (
                <Descriptions.Item label="Kutish vaqti">
                  {WAITING_STATUSES.includes(trip.status) ? (
                    <Tag icon={<ClockCircleOutlined />} color="warning" style={{ fontSize: 14 }}>
                      {formatWaitingTime(trip.requestedAt)}
                    </Tag>
                  ) : (
                    <span style={{ color: '#7F8C8D' }}>
                      {formatWaitingTime(trip.requestedAt)}
                    </span>
                  )}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>

          <Card
            title={
              <>
                <UserOutlined /> Mijoz
              </>
            }
            style={{ marginTop: 24 }}
          >
            <Descriptions column={1}>
              <Descriptions.Item label="Ism">
                {trip?.client?.fullName || 'Ismi yo\'q'}
              </Descriptions.Item>
              <Descriptions.Item label="Telefon">
                {trip?.client?.phone}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {trip?.driver && (
            <Card
              title={
                <>
                  <CarOutlined /> Haydovchi
                </>
              }
              style={{ marginTop: 24 }}
            >
              <Descriptions column={1}>
                <Descriptions.Item label="Ism">
                  {trip.driver.fullName || 'Ismi yo\'q'}
                </Descriptions.Item>
                <Descriptions.Item label="Telefon">
                  {trip.driver.phone}
                </Descriptions.Item>
                <Descriptions.Item label="Reyting">
                  {trip.driver.rating != null ? Number(trip.driver.rating).toFixed(1) : '-'}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          )}

          {trip?.vehicle && (
            <Card title="Avtomobil" style={{ marginTop: 24 }}>
              <Descriptions column={1}>
                <Descriptions.Item label="Mashina">
                  {trip.vehicle.make} {trip.vehicle.model}
                </Descriptions.Item>
                <Descriptions.Item label="Raqam">
                  {trip.vehicle.plate}
                </Descriptions.Item>
                <Descriptions.Item label="Rang">
                  {trip.vehicle.color}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          )}
        </Col>

        {/* Trip Type Specific Details */}
        {trip?.tripType === 'POSTAGE' && (
          <Col xs={24}>
            <PostageTripDetails
              trip={{
                id: trip.id,
                packageDescription: trip.packageDescription || '',
                packageWeight: trip.packageWeight,
                packageSize: trip.packageSize,
                isFragile: trip.isFragile || false,
                requiresSignature: trip.requiresSignature || false,
                insuranceValue: trip.insuranceValue,
                destinations: trip.destinations || [],
              }}
            />
          </Col>
        )}

        {trip?.tripType === 'CARGO' && (
          <Col xs={24}>
            <CargoTripDetails
              trip={{
                id: trip.id,
                cargoDescription: trip.cargoDescription || '',
                cargoType: trip.cargoType || 'GENERAL',
                weight: trip.weight || 0,
                weightUnit: trip.weightUnit || 'KG',
                length: trip.length,
                width: trip.width,
                height: trip.height,
                volume: trip.volume,
                requiresRefrigeration: trip.requiresRefrigeration || false,
                temperatureMin: trip.temperatureMin,
                temperatureMax: trip.temperatureMax,
                requiresSpecialHandling: trip.requiresSpecialHandling || false,
                handlingInstructions: trip.handlingInstructions,
                insuranceRequired: trip.insuranceRequired || false,
                insuranceValue: trip.insuranceValue,
                loadingAssistance: trip.loadingAssistance || false,
                unloadingAssistance: trip.unloadingAssistance || false,
                vehicleType: trip.vehicleType || 'TRUCK_SMALL',
              }}
            />
          </Col>
        )}
      </Row>
    </Show>
  );
};
